import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export const UserMenu: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!user) return null;

  // Prioritize full name from tblDmEmployee, then fullName, then userName
  const displayName = user.tblDmEmployee?.fullname || user.fullName || user.userName || 'User';
  const displayDepartment = user.tblDmEmployee?.departmentName || user.departmentName || '';
  const displayEmail = user.tblDmEmployee?.email || user.email || '';

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div style={{ position: 'relative' }} ref={menuRef}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '6px 12px',
          background: 'rgba(255, 255, 255, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '12px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          backdropFilter: 'blur(10px)',
          minWidth: '280px', // Increased width
          maxWidth: '350px'  // Max width for very long names
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
        }}
      >
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: '600',
          fontSize: '14px',
          boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
          flexShrink: 0
        }}>
          {displayName.charAt(0).toUpperCase()}
        </div>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'flex-start',
          flex: 1,
          minWidth: 0,
          overflow: 'hidden'
        }}>
          <span style={{ 
            fontWeight: '600', 
            color: '#2c3e50', 
            fontSize: '13px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: '100%',
            maxWidth: '200px'
          }}>
            {displayName}
          </span>
          <span style={{ 
            fontSize: '11px', 
            color: '#6c757d',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: '100%',
            maxWidth: '200px'
          }}>
            {displayDepartment}
          </span>
        </div>
        <svg 
          style={{ 
            transition: 'transform 0.2s ease',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            color: '#6c757d',
            flexShrink: 0
          }}
          width="14" 
          height="14" 
          viewBox="0 0 24 24" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M7 10l5 5 5-5z" fill="currentColor"/>
        </svg>
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: '0',
          background: 'white',
          border: '1px solid #e9ecef',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          minWidth: '320px', // Increased dropdown width
          zIndex: 1000,
          marginTop: '8px',
          overflow: 'hidden'
        }}>
          <div style={{ 
            padding: '20px',
            background: 'linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)',
            borderBottom: '1px solid #e9ecef'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: '600',
                fontSize: '16px',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
              }}>
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ 
                  display: 'block', 
                  color: '#2c3e50', 
                  fontSize: '16px', 
                  marginBottom: '2px',
                  fontWeight: '600',
                  wordWrap: 'break-word'
                }}>
                  {displayName}
                </strong>
                <p style={{ 
                  margin: '0', 
                  color: '#6c757d', 
                  fontSize: '13px',
                  fontWeight: '500',
                  wordWrap: 'break-word',
                  lineHeight: '1.3'
                }}>
                  {displayDepartment}
                </p>
              </div>
            </div>
            {displayEmail && (
              <p style={{ 
                margin: '8px 0 0 0', 
                color: '#6c757d', 
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                wordWrap: 'break-word'
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="#6c757d"/>
                </svg>
                {displayEmail}
              </p>
            )}
          </div>
          
          <div style={{ padding: '8px' }}>
            <div 
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 16px',
                cursor: 'pointer',
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                color: '#dc3545',
                fontWeight: '500',
                fontSize: '14px'
              }}
              onClick={handleLogout}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f8f9fa';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" fill="currentColor"/>
              </svg>
              <span>Đăng xuất</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
